import { useState } from "react";
import { Link } from "react-router-dom";
import { galleryEntries, galleryCategories } from "@/data/galleryImages";
import { X, ArrowRight, Palette } from "lucide-react";

export default function Gallery() {
  const [activeCat, setActiveCat] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  const filtered =
    activeCat === "all"
      ? galleryEntries
      : galleryEntries.filter((e) => e.category === activeCat);

  return (
    <section
      id="gallery"
      className="py-20 lg:py-32"
      style={{ backgroundColor: "var(--mp-paper)" }}
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-10">
          <p
            className="text-xs uppercase tracking-[0.2em] font-semibold mb-4"
            style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
          >
            Craftsmanship in Every Thread
          </p>
          <h1
            className="text-3xl md:text-4xl tracking-tight leading-tight font-medium mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
            data-testid="gallery-heading"
          >
            Gallery
          </h1>
          <div className="section-divider mx-auto" />
        </div>

        {/* Order-by-Shade CTAs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          <Link
            to="/catalogue/600-lining"
            className="block group"
            data-testid="gallery-lining-cta"
          >
            <div
              className="rounded-sm border border-[var(--mp-line)] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-all duration-300 hover:shadow-md h-full"
              style={{ backgroundColor: "var(--mp-copper-solid)" }}
            >
              <div className="flex items-start sm:items-center gap-4">
                <div
                  className="w-11 h-11 rounded-sm flex-shrink-0 flex items-center justify-center"
                  style={{ backgroundColor: "rgba(255,255,255,0.15)" }}
                >
                  <Palette className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p
                    className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-1"
                    style={{ color: "rgba(255,255,255,0.88)", fontFamily: "'Manrope', sans-serif" }}
                  >
                    Lining Catalogue
                  </p>
                  <h3
                    className="text-base sm:text-lg font-medium text-white leading-tight"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    Order Lining by Quality &amp; Shade
                  </h3>
                  <p
                    className="text-xs mt-1"
                    style={{ color: "rgba(255,255,255,0.8)", fontFamily: "'Manrope', sans-serif" }}
                  >
                    600 SD · 060 DD · 006 Mercerised · 6000 · 303 · 3000
                  </p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-white group-hover:translate-x-1 transition-transform flex-shrink-0" />
            </div>
          </Link>

          <Link
            to="/catalogue/american-crepe"
            className="block group"
            data-testid="gallery-crepe-cta"
          >
            <div
              className="rounded-sm border border-[var(--mp-line)] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-all duration-300 hover:shadow-md h-full"
              style={{ backgroundColor: "var(--mp-solid)" }}
            >
              <div className="flex items-start sm:items-center gap-4">
                <div
                  className="w-11 h-11 rounded-sm flex-shrink-0 flex items-center justify-center"
                  style={{ backgroundColor: "rgba(255,255,255,0.15)" }}
                >
                  <Palette className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p
                    className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-1"
                    style={{ color: "rgba(255,255,255,0.88)", fontFamily: "'Manrope', sans-serif" }}
                  >
                    American Crepe
                  </p>
                  <h3
                    className="text-base sm:text-lg font-medium text-white leading-tight"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    19 Charts · 700+ Shades
                  </h3>
                  <p
                    className="text-xs mt-1"
                    style={{ color: "rgba(255,255,255,0.8)", fontFamily: "'Manrope', sans-serif" }}
                  >
                    Pick by shade number, set meters, send to WhatsApp
                  </p>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-white group-hover:translate-x-1 transition-transform flex-shrink-0" />
            </div>
          </Link>
        </div>

        {/* Category filter */}
        <div
          className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-10"
          data-testid="gallery-filter-bar"
        >
          {galleryCategories.map((cat) => {
            const isActive = activeCat === cat.id;
            const count =
              cat.id === "all"
                ? galleryEntries.length
                : galleryEntries.filter((e) => e.category === cat.id).length;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCat(cat.id)}
                className="text-xs sm:text-sm px-4 py-2 rounded-sm border transition-all duration-200"
                style={{
                  borderColor: isActive ? "#101010" : "#E4DDCE",
                  backgroundColor: isActive ? "#101010" : "transparent",
                  color: isActive ? "var(--mp-on-solid)" : "var(--mp-muted)",
                  fontFamily: "'Manrope', sans-serif",
                }}
                data-testid={`gallery-filter-${cat.id}`}
              >
                {cat.label}{" "}
                <span className="opacity-70">({count})</span>
              </button>
            );
          })}
        </div>

        {/* Portrait-aligned gallery grid — every image sits in a 3:4 vertical frame */}
        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
          data-testid="gallery-grid"
        >
          {filtered.map((img, idx) => (
            <button
              key={`${img.category}-${idx}`}
              onClick={() => setLightbox(img)}
              className={`gallery-item group rounded-sm overflow-hidden border border-[var(--mp-line)] relative cursor-zoom-in animate-fade-in-up bg-[var(--mp-surface)] ${
                img.feature ? "col-span-2" : ""
              }`}
              style={{ animationDelay: `${Math.min(idx, 12) * 60}ms`, aspectRatio: "3 / 4" }}
              data-testid={`gallery-image-${idx}`}
            >
              <img
                src={img.thumb}
                alt={img.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
              />
              <div
                className="absolute bottom-0 left-0 right-0 p-2 sm:p-3 text-left opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: "linear-gradient(to top, rgba(0,0,0,0.75), transparent)" }}
              >
                <p
                  className="text-[10px] sm:text-xs text-white font-medium truncate"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  {img.alt}
                </p>
              </div>
            </button>
          ))}
        </div>

        {filtered.length === 0 && (
          <p
            className="text-center text-sm mt-10"
            style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
          >
            No photos in this category yet.
          </p>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-fade-in"
          style={{ backgroundColor: "rgba(0,0,0,0.92)" }}
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 transition"
            aria-label="Close"
            data-testid="gallery-lightbox-close"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          <img
            src={lightbox.url}
            alt={lightbox.alt}
            className="max-w-full max-h-[85vh] object-contain rounded-sm"
            onClick={(e) => e.stopPropagation()}
          />
          <p
            className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs sm:text-sm text-white/80 px-4 text-center"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            {lightbox.alt}
          </p>
        </div>
      )}
    </section>
  );
}
